import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { parsePaging, parseSorting } from '../../utils/queryString';
import useExtendedQuery from '../../hooks/useExtendedQuery';
import Spinner from '../Spinner';
import Paginator from '../Paginator';
import { ALL_AUCTION_LIMITS, ON_AUCTION_LIMIT_CHANGED } from '../../graphql/auctionLimits';
import AuctionLimitsList from './AuctionLimitsList';
import Error from '../errors/Error';
import CreateAuctionLimitRow from './CreateAuctionLimitRow';
import SubscriptionActions from '../../utils/SubscriptionActions';
import useExtendedSubscription from '../../hooks/useExtendedSubscription';
import { showInfoNotification } from '../../actions/notifications';
import Constants from '../../utils/Constants';
import { hasPrivilege } from '../../hocs/withPrivileges';
import Privileges from '../../utils/Privileges';
import useAuthentication from '../../hooks/useAuthentication';

export function createNotificationText(t, action, memberName, memberId, productName, productId) {
    let notification;
    switch (action) {
        case SubscriptionActions.AUCTION_LIMIT_CREATED:
            notification = t('auction-limits.notification.auction-limit-create-thirdparty', {
                memberName,
                memberId,
                productName,
                productId,
            });
            break;
        case SubscriptionActions.AUCTION_LIMIT_UPDATED:
            notification = t('auction-limits.notification.auction-limit-update-thirdparty', {
                memberName,
                memberId,
                productName,
                productId,
            });
            break;
        case SubscriptionActions.AUCTION_LIMIT_DELETED:
            notification = t('auction-limits.notification.auction-limit-delete-thirdparty', {
                memberName,
                memberId,
                productName,
                productId,
            });
            break;
        default:
            notification = t('auction-limits.notification.auction-limits-modified-thirdparty');
    }
    return notification;
}

function AuctionLimits() {
    const location = useLocation();
    const { t } = useTranslation();
    const { userPrivileges } = useAuthentication();
    const { page, perPage } = parsePaging(location.search);
    const {
        sortKey = 'member.memberId',
        sortDirection = Constants.SORT_ASC,
    } = parseSorting(location.search);
    const dispatch = useDispatch();
    const [refetchProps, setRefetchProps] = useState({
        perPage,
        page,
        sortKey,
        sortDirection,
    });
    const {
        loading,
        error,
        data,
        refetch,
    } = useExtendedQuery(ALL_AUCTION_LIMITS, {
        variables: refetchProps,
    });
    const onPageChange = (newPage) => setRefetchProps({
        ...refetchProps,
        page: newPage,
    });
    const onSort = (newSorting) => setRefetchProps({
        ...refetchProps,
        sortKey: newSorting.key,
        sortDirection: newSorting.direction,
    });
    useExtendedSubscription(ON_AUCTION_LIMIT_CHANGED, {
        refetch,
        refetchVariables: refetchProps,
        dataProperty: 'auctionLimitChanged',
        onChangeCallback: ({ action, model: { member: { name: memberName, memberId }, product: { name: productName, productId } } }) => {
            if (hasPrivilege(userPrivileges, [Privileges.AUCTION_LIMIT_EDIT])) {
                const notification = createNotificationText(t, action, memberName, memberId, productName, productId);
                dispatch(showInfoNotification(notification));
            }
        },
    });
    let result;
    let auctionLimitProps = null;
    if (loading) {
        result = <Spinner className="auction-limits-loading" />;
    } else if (error) {
        result = <Error className="auction-limits-error" error={error} />;
    } else {
        auctionLimitProps = {
            availableProducts: data.allAuctionLimits.availableProducts,
            availableMembers: data.allAuctionLimits.availableMembers,
            minAuctionLimit: data.allAuctionLimits.minAuctionLimit,
            maxAuctionLimit: data.allAuctionLimits.maxAuctionLimit,
        };
        result = (
            <Paginator
                totalCount={data.allAuctionLimits.auctionLimitList.totalCount}
                perPage={perPage}
                currentPage={refetchProps.page}
                onPageChange={onPageChange}
                bottom
            >
                <AuctionLimitsList
                    auctionLimits={data.allAuctionLimits.auctionLimitList.models}
                    onSort={onSort}
                    auctionLimitProps={auctionLimitProps}
                    sortingParam={{ key: refetchProps.sortKey, direction: refetchProps.sortDirection }}
                />
            </Paginator>
        );
    }
    return (
        <>
            <h4>
                <span className="fa-layers fa-fw left-menu--item--icon">
                    <FontAwesomeIcon icon="gavel" transform="shrink-7 right-6 down-2" />
                    <FontAwesomeIcon icon="filter" transform="left-2" />
                </span>
                &nbsp;{t('title.auction-limits')}
            </h4>
            {auctionLimitProps && <CreateAuctionLimitRow auctionLimitProps={auctionLimitProps} />}
            {result}
        </>
    );
}

export default AuctionLimits;
